import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { InfoCircle } from '@saleshandy/icons';
import { CallLog, GetCallLogsListPayload } from '../../../../types/call-logs';
import ProspectNameCell from './cells/prospect-name-cell';
import TranscriptCell from './cells/transcript-cell';
import CallOutcomeCell from './cells/call-outcome-cell';
import DateTimeCell from './cells/date-time-cell';
import { formatDuration } from '../../utils/helpers';
import { CallManagementState } from '../../../../../../shared/components/dialer/types';
import AudioPlayer from '../../../audio-player/audio-player';
import Button from '../../../../../../shared/design-system/ui/button';
import { Trash } from '../../../../../../shared/svg/trash';
import { accessibleOnClick, toTelHref } from '../../../../../../shared/utils';
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay';
import { getSignedUrlForCallRecordingRequest } from '../../../../extra-actions';
import { RootState } from '../../../../../../store/root-reducer';
import TruncatedTextWithAutoTooltip from '../../../../../../shared/components/truncated-text-with-auto-tooltip';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import { SortOrder } from '../../../../../../shared/enums/order';
import { DialerListSortType } from '../../../../enums/prospect';
import ReportsSortableHeader from '../reports/components/reports-incoming-table/components/reports-sortable-header';

const columnHelper = createColumnHelper<CallLog>();

export interface GetCallLogsColumnDefinitionsParams {
  tabType: GetCallLogsListPayload['type'];
  openVoicemailId: number | null;
  setOpenVoicemailId: React.Dispatch<React.SetStateAction<number | null>>;
  currentCallManagementState: CallManagementState;
  role?: string;
  onDeleteClick?: (row: CallLog) => void;
  activeCallLogsTab?: string;
  sendAssignCallOutcomeRequest?: (
    outcomeId: number,
    dialerCallLogId: number,
  ) => void;
  setSelectedTemplateId?: (id: number | null) => void;
  selectedTemplateId?: number | null;
  onProspectNameClick?: (ctx: { columnId: number; row: CallLog }) => void;
  onMarkSingleCallAsSeen?: (callId: number) => void;
  sort?: { sortBy: string; order: SortOrder } | null;
  onSort?: (sortBy: string, order: SortOrder) => void;
}

const VoicemailInlineAudioCell: React.FC<{
  row: CallLog;
}> = ({ row }) => {
  const dispatch = useDispatch();
  // Need to preserve the call recording Url in tab switch until user reloads the page(Use this)
  const signedUrl = useSelector(
    (state: RootState) =>
      state?.dialer?.getSignedUrlForCallRecordingResponses?.[row?.callId]
        ?.signedUrl ?? null,
  );
  // IF no need to preserve the call recording Url in tab switch until (Use this)
  // const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);

  const handlePlayWithoutSrc = useCallback(async () => {
    const callIdNumber = Number(row.callId);
    if (!Number.isFinite(callIdNumber)) return;

    setShouldAutoPlay(true);
    setIsLoading(true);
    try {
      const action: any = await dispatch(
        getSignedUrlForCallRecordingRequest(callIdNumber) as any,
      );
      const payload = action?.payload?.payload ?? action?.payload;
      if (!payload?.signedUrl) {
        setIsLoading(false);
        // setSignedUrl(payload.signedUrl as string);
      }
    } catch {
      setIsLoading(false);
    }
  }, [dispatch, row.callId]);

  const audioSrc = signedUrl || row?.recordingUrl || null;

  return (
    <AudioPlayer
      pauseOnScroll={false}
      defaultDuration={row?.recordingDuration}
      audioSrc={audioSrc}
      className={`voicemail-greeting-audio-player ${
        isLoading ? 'audio-player-loading' : ''
      }`}
      showPlaybackSpinner={isLoading}
      onPlayWithoutSrc={handlePlayWithoutSrc}
      autoPlayWhenSrcAvailable={shouldAutoPlay}
      onAutoPlayCompleted={() => {
        setShouldAutoPlay(false);
        setIsLoading(false);
      }}
    />
  );
};

export const getCallLogsColumnDefinitions = ({
  tabType,
  openVoicemailId,
  setOpenVoicemailId,
  currentCallManagementState,
  // role,
  onDeleteClick,
  activeCallLogsTab,
  sendAssignCallOutcomeRequest,
  setSelectedTemplateId,
  selectedTemplateId,
  onProspectNameClick,
  onMarkSingleCallAsSeen,
  sort,
  onSort,
}: GetCallLogsColumnDefinitionsParams) => {
  const useSortableHeader = !!(sort !== undefined && onSort);

  const wrapOpenCallLogDetail = (
    row: CallLog,
    children: React.ReactNode,
    wrapperClassName = 'd-flex align-items-center cursor-pointer call-logs-column-content',
  ) => {
    if (!onProspectNameClick) {
      return <>{children}</>;
    }
    return (
      <span
        className={wrapperClassName}
        {...accessibleOnClick(() =>
          onProspectNameClick({ columnId: row.id, row }),
        )}
      >
        {children}
      </span>
    );
  };

  const wrapOpenCallLogDetailIf = (
    row: CallLog,
    isActionable: boolean,
    children: React.ReactNode,
    wrapperClassName = 'd-flex align-items-center cursor-pointer call-logs-column-content',
  ) => {
    if (!onProspectNameClick || !isActionable) {
      return (
        <span className="call-logs-column-content-no-value cursor-default">
          {children}
        </span>
      );
    }
    return wrapOpenCallLogDetail(row, children, wrapperClassName);
  };

  // Common columns reused across tab types
  const prospectNameColumn = columnHelper.accessor('prospectName', {
    id: 'prospectName',
    header: 'Prospect Name',
    cell: ({ row }) => {
      const { original } = row;
      return wrapOpenCallLogDetail(
        row?.original,
        <ProspectNameCell
          row={original}
          isShowVoicemailPopover={openVoicemailId === original?.id}
          setShowVoicemailPopover={setOpenVoicemailId}
          currentCallManagementState={currentCallManagementState}
          activeCallLogsTab={activeCallLogsTab}
          // onProspectNameClick={onProspectNameClick}
          onMarkAsSeen={onMarkSingleCallAsSeen}
        />,
      );
    },
    size: 300,
    minSize: 300,
    maxSize: 400,
    enableSorting: false,
  });

  const dateTimeColumn = columnHelper.accessor('dateTime', {
    id: 'dateTime',
    header:
      useSortableHeader && onSort
        ? () => (
            <ReportsSortableHeader
              label="Date & Time"
              columnId={DialerListSortType.CALL_DATE_TIME}
              sortBy={DialerListSortType.CALL_DATE_TIME}
              sort={sort ?? null}
              onSort={onSort}
            />
          )
        : 'Date & Time',
    cell: ({ row }) => {
      const { original } = row;
      return wrapOpenCallLogDetailIf(
        original,
        Boolean(original?.dateTime),
        <DateTimeCell row={original} />,
      );
    },
    size: 210,
    enableSorting: false,
  });

  const callerHostColumn = columnHelper.accessor('callerHost', {
    id: 'callerHost',
    header: 'Caller/Host',
    cell: ({ getValue, row }) => {
      const raw = getValue();
      const hasText = Boolean(String(raw ?? '').trim());
      return wrapOpenCallLogDetailIf(
        row.original,
        hasText,
        <TruncatedTextWithAutoTooltip text={raw || '-'} />,
      );
    },
    size: 200,
    enableSorting: false,
  });

  const prospectPhoneColumn = columnHelper.accessor('prospectPhone', {
    id: 'prospectPhone',
    header: 'Prospect Phone Number',
    cell: ({ getValue, row }) => {
      const { original } = row;
      const num = getValue()?.number?.trim() || '';
      if (!num) {
        return '-';
      }
      const telHref = onProspectNameClick ? undefined : toTelHref(num);
      return wrapOpenCallLogDetail(
        original,
        <TruncatedTextWithAutoTooltip
          className="blue-txt-15 prospect-phone-number-column-content"
          text={num}
          telHref={telHref ?? undefined}
        />,
      );
    },
    size: 180,
    enableSorting: false,
  });

  const canDeleteCallLog = hasPermission(Permissions.PHONE_CALL_DELETE);
  const deleteColumn: ColumnDef<CallLog, any> | undefined =
    canDeleteCallLog && onDeleteClick
      ? columnHelper.display({
          id: 'delete',
          header: '',
          cell: ({ row }) => (
            <Button
              {...accessibleOnClick(() => onDeleteClick(row.original))}
              aria-label="Delete call log"
              className="delete-call-log-button"
            >
              <Trash width={20} height={20} />
            </Button>
          ),
          size: 56,
          minSize: 56,
          maxSize: 56,
          enableSorting: false,
        })
      : undefined;

  if (tabType === 'missed') {
    // Missed Calls: limited column set
    const columns: ColumnDef<CallLog, any>[] = [
      prospectNameColumn,
      dateTimeColumn,
      callerHostColumn,
      prospectPhoneColumn,
    ];

    if (deleteColumn) {
      columns.push(deleteColumn);
    }

    return columns;
  }

  if (tabType === 'voicemails') {
    // Incoming Voicemails: Voicemail & Transcript focused view
    const voicemailColumn = columnHelper.accessor('hasVoicemail', {
      id: 'hasVoicemail',
      header: 'Voicemail',
      cell: ({ row }) =>
        wrapOpenCallLogDetail(
          row?.original,
          <VoicemailInlineAudioCell row={row.original} />,
        ),
      size: 220,
      minSize: 80,
      maxSize: 200,
      enableSorting: false,
    });
    const transcriptColumn = columnHelper.accessor('transcription', {
      id: 'transcription',
      header: 'Transcript',
      cell: ({ getValue, row }) => {
        const { original } = row;
        const value = getValue() as string | null;
        const hasText = Boolean(String(value ?? '').trim());
        return wrapOpenCallLogDetailIf(
          row?.original,
          hasText,
          <TranscriptCell
            transcript={value}
            original={original}
            setSelectedTemplateId={setSelectedTemplateId}
            selectedTemplateId={selectedTemplateId}
          />,
        );
      },
      size: 260,
      minSize: 160,
      maxSize: 400,
      enableSorting: false,
    });

    const columns: ColumnDef<CallLog, any>[] = [
      prospectNameColumn,
      voicemailColumn,
      transcriptColumn,
      dateTimeColumn,
      callerHostColumn,
      prospectPhoneColumn,
    ];

    if (deleteColumn) {
      columns.push(deleteColumn);
    }

    return columns;
  }

  // Default: All Calls - full detail view
  const durationLabel = (
    <div className="d-flex align-items-center gap-4">
      <span>Call Duration</span>
      <OverlayTooltip text="Call duration is the total time from when a call is successfully connected to when it ends.">
        <InfoCircle width={14} height={14} color="#6b7280" cursor="pointer" />
      </OverlayTooltip>
    </div>
  );

  const durationColumn = columnHelper.accessor('callDuration', {
    id: 'callDuration',
    header:
      useSortableHeader && onSort
        ? () => (
            <ReportsSortableHeader
              label={durationLabel}
              columnId={DialerListSortType.CALL_DURATION}
              sortBy={DialerListSortType.CALL_DURATION}
              sort={sort ?? null}
              onSort={onSort}
            />
          )
        : () => durationLabel,
    cell: (info) => {
      const value = info.getValue();
      const hasValue = value != null;
      const content = hasValue ? formatDuration(value) : '-';
      return wrapOpenCallLogDetailIf(info.row.original, hasValue, content);
    },
    size: 180,
    minSize: 160,
    maxSize: 240,
    enableSorting: false,
  });

  const ringTimeColumn = columnHelper.accessor('ringDuration', {
    id: 'ringDuration',
    header:
      useSortableHeader && onSort
        ? () => (
            <ReportsSortableHeader
              label="Ring Time"
              columnId={DialerListSortType.RING_TIME}
              sortBy={DialerListSortType.RING_TIME}
              sort={sort ?? null}
              onSort={onSort}
            />
          )
        : 'Ring Time',
    cell: (info) => {
      const value = info.getValue();
      const hasValue = value != null;
      const content = hasValue ? formatDuration(value) : '-';
      return wrapOpenCallLogDetailIf(info.row.original, hasValue, content);
    },
    size: 180,
    minSize: 150,
    maxSize: 240,
    enableSorting: false,
  });

  const canUpdateCallOutcome = hasPermission(Permissions.PHONE_CALL_UPDATE);
  const callOutcomeColumn = columnHelper.accessor('callOutcome', {
    id: 'callOutcome',
    header: 'Call Outcome',
    cell: ({ row }) => {
      const callOutcome = row?.original;
      return wrapOpenCallLogDetail(
        row?.original,
        <CallOutcomeCell
          row={callOutcome}
          onChangeCallOutcome={(outcomeId: number, callLogId: number) =>
            sendAssignCallOutcomeRequest?.(outcomeId, callLogId)
          }
          disabled={!canUpdateCallOutcome}
        />,
      );
    },
    size: 220,
    minSize: 200,
    maxSize: 300,
    enableSorting: false,
  });

  const sourceColumn = columnHelper.accessor('source', {
    id: 'source',
    header: 'Source',
    cell: ({ getValue, row }) => {
      const raw = getValue();
      const hasText = Boolean(String(raw ?? '').trim());
      return wrapOpenCallLogDetailIf(
        row.original,
        hasText,
        <TruncatedTextWithAutoTooltip text={raw || '-'} />,
      );
    },
    size: 180,
    minSize: 100,
    maxSize: 280,
    enableSorting: false,
  });

  const sequenceColumn = columnHelper.accessor('sequenceTitle', {
    id: 'sequenceTitle',
    header: () => (
      <div className="d-flex align-items-center gap-4">
        <span>Sequence Associated</span>
        <OverlayTooltip text="When a sequence has a call step, it creates a task. If a call is made from that task, the sequence is automatically linked to this call log. Each log maps to one sequence only.">
          <InfoCircle width={14} height={14} color="#6b7280" cursor="pointer" />
        </OverlayTooltip>
      </div>
    ),
    cell: ({ getValue, row }) => {
      const raw = getValue();
      const hasText = Boolean(String(raw ?? '').trim());
      return wrapOpenCallLogDetailIf(
        row.original,
        hasText,
        <TruncatedTextWithAutoTooltip text={raw || '-'} />,
      );
    },
    size: 220,
    minSize: 140,
    maxSize: 340,
    enableSorting: false,
  });

  const columns: ColumnDef<CallLog, any>[] = [
    prospectNameColumn,
    durationColumn,
    ringTimeColumn,
    callOutcomeColumn,
    dateTimeColumn,
    callerHostColumn,
    sourceColumn,
    sequenceColumn,
    prospectPhoneColumn,
  ];

  if (deleteColumn) {
    columns.push(deleteColumn);
  }

  return columns;
};
